import React, { useState, useEffect, useRef } from 'react';
import BottomSheet from './BottomSheet';
import Avatar from './Avatar';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, isUsernameAvailable } from '../lib/database';
import { getBadgeByKey } from '../lib/badgeDefinitions';

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

function EditProfileSheet({ onClose, activeTitleKey = null }) {
  const { profile, setProfile } = useAuth();

  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');
  const [username, setUsername] = useState(profile?.username || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [isPublic, setIsPublic] = useState(!!profile?.is_public);

  const [usernameState, setUsernameState] = useState('idle'); // idle|checking|ok|taken|invalid
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const checkSeq = useRef(0);

  const title = activeTitleKey ? getBadgeByKey(activeTitleKey) : null;
  const normalizedUsername = username.trim().toLowerCase();
  const usernameChanged = normalizedUsername !== (profile?.username || '').toLowerCase();

  // Verifica disponibilità username (debounced) quando cambia
  useEffect(() => {
    if (!usernameChanged) { setUsernameState('idle'); return; }
    if (!USERNAME_RE.test(normalizedUsername)) { setUsernameState('invalid'); return; }

    setUsernameState('checking');
    const seq = ++checkSeq.current;
    const t = setTimeout(async () => {
      const available = await isUsernameAvailable(normalizedUsername, profile.id);
      if (seq !== checkSeq.current) return; // risposta obsoleta
      setUsernameState(available ? 'ok' : 'taken');
    }, 450);
    return () => clearTimeout(t);
  }, [normalizedUsername, usernameChanged, profile?.id]);

  const canSave =
    !saving &&
    (!usernameChanged || usernameState === 'ok') &&
    (normalizedUsername === '' || USERNAME_RE.test(normalizedUsername));

  const handleSave = async () => {
    setError('');
    // Se pubblico, lo username è obbligatorio
    if (isPublic && !normalizedUsername) {
      setError('Per un profilo pubblico serve uno username.');
      return;
    }
    if (normalizedUsername && !USERNAME_RE.test(normalizedUsername)) {
      setError('Username non valido: 3-20 caratteri, solo lettere minuscole, numeri e _.');
      return;
    }

    setSaving(true);
    const composedName =
      `${firstName.trim()} ${lastName.trim()}`.trim() || profile?.name || 'Atleta';
    const updates = {
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      name: composedName,
      username: normalizedUsername || null,
      bio: bio.trim() || null,
      is_public: isPublic,
    };

    try {
      await updateProfile(profile.id, updates);
      setProfile({ ...profile, ...updates });
      onClose();
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('23505') || msg.includes('duplicate') || msg.includes('unique')) {
        setError('Username già in uso, scegline un altro.');
        setUsernameState('taken');
      } else {
        setError('Errore nel salvataggio. Riprova.');
      }
      setSaving(false);
    }
  };

  const footer = (
    <button
      type="button"
      onClick={handleSave}
      disabled={!canSave}
      className={`w-full py-3.5 rounded-2xl font-semibold text-base ${
        canSave ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'
      }`}
    >
      {saving ? 'Salvataggio…' : 'Salva'}
    </button>
  );

  return (
    <BottomSheet onClose={onClose} zIndexClass="z-[1200]" closeButton footer={footer}>
      <div className="px-6 pt-1 pb-4 space-y-5">
        <div className="flex flex-col items-center">
          <Avatar
            name={`${firstName} ${lastName}`.trim() || profile?.name}
            seed={profile?.id}
            size="w-20 h-20"
            textClass="text-2xl"
          />
          <p className="text-xs text-gray-400 mt-2">La foto profilo arriverà prossimamente</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Nome">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Mario"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"
            />
          </Labeled>
          <Labeled label="Cognome">
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Rossi"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"
            />
          </Labeled>
        </div>

        <Labeled label="Username" hint={usernameHint(usernameState)}>
          <div className="flex items-center rounded-xl border border-gray-200 px-3">
            <span className="text-gray-400 text-sm">@</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
              placeholder="mario_rossi"
              autoCapitalize="none"
              autoCorrect="off"
              className="flex-1 px-2 py-2.5 text-sm bg-transparent outline-none lowercase"
            />
            {usernameState === 'checking' && <span className="text-xs text-gray-400">…</span>}
            {usernameState === 'ok' && <span className="text-xs text-green-600 font-bold">✓</span>}
            {usernameState === 'taken' && <span className="text-xs text-red-500 font-bold">✕</span>}
          </div>
        </Labeled>

        <Labeled label="Bio">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 160))}
            placeholder="Racconta qualcosa su di te…"
            rows={3}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm resize-none"
          />
          <p className="text-[11px] text-gray-400 text-right mt-1">{bio.length}/160</p>
          <p className="text-[11px] text-gray-400 mt-1">Evita di pubblicare informazioni personali o sanitarie sensibili.</p>
        </Labeled>

        {/* Privacy */}
        <button
          type="button"
          onClick={() => setIsPublic((v) => !v)}
          className="w-full flex items-center justify-between rounded-2xl border border-gray-200 px-4 py-3"
        >
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-800">
              {isPublic ? 'Profilo pubblico' : 'Profilo privato'}
            </p>
            <p className="text-xs text-gray-400">
              {isPublic
                ? 'Visibile nella Community e ricercabile'
                : 'Nascosto dalla Community'}
            </p>
          </div>
          <span
            className={`w-11 h-6 rounded-full relative transition-colors ${
              isPublic ? 'bg-primary-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${
                isPublic ? 'left-[22px]' : 'left-0.5'
              }`}
            />
          </span>
        </button>

        {/* Titolo attivo (impostato dalla collezione badge) */}
        <div className="rounded-2xl bg-gray-50 px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Titolo attivo</p>
          {title ? (
            <p className="text-sm font-semibold text-primary-600 flex items-center gap-1.5">
              <span>{title.icon}</span>
              <span>{title.name}</span>
            </p>
          ) : (
            <p className="text-sm text-gray-500">Nessuno — scegline uno dalla collezione badge</p>
          )}
        </div>

        {error && <p className="text-sm text-red-500 text-center">{error}</p>}
      </div>
    </BottomSheet>
  );
}

function Labeled({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-gray-500 mb-1 flex items-center justify-between">
        <span>{label}</span>
        {hint && <span className="font-normal text-gray-400">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

function usernameHint(state) {
  switch (state) {
    case 'invalid': return '3-20 char: a-z, 0-9, _';
    case 'taken': return 'già in uso';
    case 'ok': return 'disponibile';
    default: return '';
  }
}

export default EditProfileSheet;
